import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import equals from 'ramda/src/equals';
import find from 'ramda/src/find';
import isNil from 'ramda/src/isNil';
import keys from 'ramda/src/keys';
import path from 'ramda/src/path';
import propEq from 'ramda/src/propEq';
import originalMoment from 'moment';
import { extendMoment } from 'moment-range';
import queryString from 'query-string';
import DatePicker from '@components/@core/DatePicker';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import EditDropdown from '@components/EditDropdown';
import Filter from '@components/@core/Filter';
import ResponsiveTabs from '@components/@core/ResponsiveTabs';
import Tab from '@components/@core/Tab';
import AppActions from '~/actions/app';
import CommunityActions from '~/actions/community';
import ManageActions from '~/actions/manage';
import { DailyCheckoutTabs } from '~/services/config';
import AverageScore from './AverageScore';
import EmployeeList from './EmployeeList';
import OrgChart from './OrgChart';
import { EmployeePdf } from './EmployeesPDF';
import { OrgChartPdf } from './OrgChartsPDF';

const moment = extendMoment(originalMoment);

const DailyCheckout = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const selectedCompanyId = useSelector((state) => state.app.selectedCompany);
    const companies = useSelector((state) => state.app.companies);
    const employees = useSelector((state) => state.app.users);
    const reviews = useSelector((state) => state.manage.companyCheckouts);
    const checkoutActuals = useSelector((state) => state.manage.checkoutActuals);
    const parsedCompanyIds = equals([-1], selectedCompanyId) ? companies.map((e) => e.id) : selectedCompanyId;
    const selectedCompany = find(propEq('id', parsedCompanyIds[0]))(companies);

    const [companyId, setCompanyId] = useState(parsedCompanyIds);
    const [userId, setUserId] = useState(employees.map((e) => e.id));
    const [date, setDate] = useState(moment());
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(selectedCompany?.users?.length || 25);
    const [active, setActive] = useState(queryString.parse(location.search)?.active ?? 'chart');

    useEffect(() => {
        dispatch(ManageActions.getcheckoutactualsRequest({ companyId, date: moment(date).format('YYYY-MM-DD') }));
        handleFetch(false, companyId, null, page, perPage, moment(date).format('YYYY-MM-DD'));
    }, [page, perPage]);

    const handleTab = useCallback(
        (e) => {
            if (e.name === active) return;

            if (e.name === 'employees') {
                handleFetch(true, companyId, userId, page, perPage, moment(date).format('YYYY-MM-DD'));
            } else {
                dispatch(ManageActions.getcheckoutactualsRequest({ companyId, date: moment(date).format('YYYY-MM-DD') }));
            }
            navigate({ search: `?active=${e.name}` });
            setActive(e.name);
        },
        [active]
    );

    const handleDetail = (scorecard) => (e) => {
        e.stopPropagation();
        if (scorecard) {
            const date = moment(date);
            const payload = { type: 'Scorecard Detail', data: { before: { scorecard, date } } };
            dispatch(AppActions.modalRequest(payload));
        }
    };

    const handleDate = useCallback(
        (e) => {
            setDate(e);
            if (active === 'employees') {
                handleFetch(true, companyId, userId, page, perPage, moment(e).format('YYYY-MM-DD'));
            } else {
                dispatch(ManageActions.getcheckoutactualsRequest({ companyId, date: moment(e).format('YYYY-MM-DD') }));
            }
        },
        [active, companyId, userId, page, perPage]
    );

    const handleFetch = (loading = true, companyId, userId, page, perPage, date, order = 'name', sort = 'ASC') => {
        const payload = {
            companyId,
            userId,
            page,
            perPage,
            dateStart: moment(date).format('YYYY-MM-DD'),
            dateEnd: moment(date).format('YYYY-MM-DD'),
            order: 'name',
            sort: 'ASC',
        };
        dispatch(ManageActions.getcompanycheckoutsRequest(payload, null, loading));
    };

    const handleFilter = (type, e) => {
        if (type === 'company') {
            const companyIds = e.map((item) => item.id);
            handleFetch(true, companyIds, userId, page, perPage, moment(date).format('YYYY-MM-DD'));
            setCompanyId(companyIds);
        } else if (type === 'employee') {
            const userIds = e.map((item) => item.id);
            handleFetch(true, companyId, userIds, page, perPage, moment(date).format('YYYY-MM-DD'));
            setUserId(userIds);
        }
    };

    const handleSelectMenu = (data) => (e) => {
        const { daily_scorecard } = data;
        const scorecards = daily_scorecard ? [daily_scorecard] : [];
        const user = data?.user?.community_user ?? find(propEq('id', data?.user?.id), employees);
        const compId = scorecards.length > 0 && scorecards[0].company_id ? scorecards[0].company_id : data.user.company_id;
        switch (e) {
            case 'checkout':
                const payload = {
                    userId: data.user.id,
                    companyId: data.company.id,
                    dateStart: moment(date).format('YYYY-MM-DD'),
                    dateEnd: moment(date).format('YYYY-MM-DD'),
                };
                const route = {
                    pathname: `/hcm/daily-checkout/${data.user.id}`,
                    search: `date=${moment(date).format('YYYY-MM-DD')}`,
                    state: { lastReview: null },
                };
                dispatch(ManageActions.individualperformancereviewRequest(payload, route));
                break;

            case 'save actuals':
                dispatch(
                    AppActions.modalRequest({
                        type: 'Save Daily Actuals',
                        data: {
                            before: {
                                user: { ...user, company_id: compId },
                                date: moment(date).format('YYYY-MM-DD'),
                            },
                        },
                    })
                );
                break;

            case 'employee profile': {
                if (user.community_user?.entity?.slug) {
                    const payload = { userId: user.community_user?.id };
                    const route = `/${user.community_user?.entity?.slug}/about?user_id=${user.community_user_id}`;
                    dispatch(CommunityActions.getcommunityuserRequest(payload, null, route));
                } else {
                    toast.error(`The User ${user?.first_name} ${user?.last_name} does not have a community profile.`, {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                }
                break;
            }

            case 'assign scorecard':
                handleAssign(data, user);
                break;

            case 'preview scorecard': {
                const payload = {
                    type: 'Scorecard Detail',
                    data: { before: { scorecard: scorecards[0], date: moment(date) } },
                };
                dispatch(AppActions.modalRequest(payload));
                break;
            }

            case 'unassign scorecard': {
                dispatch(
                    AppActions.modalRequest({
                        type: 'Quick Edit',
                        data: {
                            before: {
                                template: scorecards.length ? scorecards : {},
                                userId: user.id,
                                type: 'performanceScorecards',
                                from: 'template',
                            },
                        },
                        callBack: {
                            onAssign: () => handleAssign(data, user),
                            onDelete: (e) => handleUnassignScorecard(e),
                        },
                    })
                );
            }

            default:
                break;
        }
    };

    const handlePerformanceReport = (item) => (e) => {
        e.stopPropagation();
        const uId = item.user.id;
        const compId = !isNil(item?.daily_scorecard) && item?.daily_scorecard?.company_id ? item.daily_scorecard.company_id : Number(keys(item.user.data.company)[0]);
        const payload = { company_id: compId, user_id: uId, date: moment(date).format('YYYY-MM-DD') };
        const route = {
            pathname: `/hcm/daily-checkout/${uId}`,
            search: `date=${moment(date).format('YYYY-MM-DD')}`,
        };
        dispatch(ManageActions.getcheckoutRequest(payload, route));
    };

    const handleUnassignScorecard = (e) => {
        if (isNil(e)) return;
        dispatch(
            AppActions.modalRequest({
                type: 'Confirm',
                data: {
                    before: {
                        title: 'Unassign Scorecard',
                        body: 'This will unassign the Scorecard from the User. Are you sure you wish to proceed?',
                    },
                },
                callBack: {
                    onYes: () => {
                        dispatch(ManageActions.postdeletescorecardRequest({ scorecard: { id: e.id } }));
                        if (active === 'employees') {
                            handleFetch(true, companyId, userId, page, perPage, moment(date).format('YYYY-MM-DD'));
                        } else {
                            dispatch(ManageActions.getcheckoutactualsRequest({ companyId, date: moment(date).format('YYYY-MM-DD') }));
                        }
                    },
                },
            })
        );
    };

    const handleAssign = useCallback(
        (e, user) => {
            const after = {
                type: 'GETCOMPANYCHECKOUTS_REQUEST',
                payload: {
                    companyId,
                    userId,
                    page,
                    perPage,
                    dateStart: moment(date).format('YYYY-MM-DD'),
                    dateEnd: moment(date).format('YYYY-MM-DD'),
                },
            };
            const payload = {
                type: 'Assign ScoreCard',
                data: {
                    before: {
                        modules: [],
                        disabled: ['monthly', 'quarterly', 'yearly'],
                        assignees: [e.user.id],
                        date,
                        after,
                        user,
                    },
                },
            };
            dispatch(AppActions.modalRequest(payload));
        },
        [companyId, userId, page, perPage, date]
    );

    const handleRowClick = (item, type) => () => {
        handleSelectMenu(item)(type);
    };

    const handleSortEmployees = (e) => {
        const { order, sort } = e;
        handleFetch(true, companyId, userId, page, perPage, moment(date).format('YYYY-MM-DD'), order, sort);
    };

    const handleReportMenu = (e) => {
        switch (e) {
            case 'print':
                if (active === 'chart') {
                    const dgroups = companyId.map((id) => path(['companies', id], checkoutActuals));
                    OrgChartPdf(dgroups[0]);
                } else {
                    EmployeePdf(reviews);
                }
                break;
        }
    };

    return (
        <ErrorBoundary className="vdmm-cp">
            <div className="page-filter">
                <Filter defaultIds={[[], [-1]]} withAllEmployees onChange={handleFilter} />
            </div>
            <div className="card">
                <div className="d-h-between">
                    <span className="dsl-b22 bold">Daily Checkout</span>
                    <div className="d-flex">
                        <DatePicker calendar="day" append="caret" format="MMM DD, YYYY" as="span" align="right" value={date} maxDate={new Date()} onSelect={handleDate} />
                        <EditDropdown className="ml-3" dataCy="editDropdown" options={['Print']} onChange={handleReportMenu} />
                    </div>
                </div>
                <ResponsiveTabs active={active} data={DailyCheckoutTabs} getId={(e) => e.name} getValue={(e) => e.label} onTab={handleTab} />
                <Tab eventKey="employees" active={active}>
                    <AverageScore
                        className="mt-3"
                        data={{
                            average_star_rating: reviews?.average_star_rating || 0,
                            checkouts_completed: reviews?.checkouts_completed || 0,
                            checkouts_total: reviews?.checkouts_total || 0,
                            total: reviews?.total || 0,
                        }}
                        type="employee"
                    />
                    <EmployeeList
                        data={reviews}
                        checkoutActuals={checkoutActuals}
                        companyId={companyId}
                        date={date}
                        pagination
                        onRowClick={handleRowClick}
                        onSelectMenu={handleSelectMenu}
                        onReport={handlePerformanceReport}
                        onScorecardDetail={handleDetail}
                        onPagination={(e) => setPage(e)}
                        onPer={(e) => setPerPage(e)}
                        onSort={handleSortEmployees}
                    />
                </Tab>
                <Tab eventKey="chart" active={active}>
                    <AverageScore className="mt-3" data={checkoutActuals?.totals} type="org" />
                    <OrgChart
                        data={checkoutActuals}
                        companyId={companyId}
                        date={date}
                        onRowClick={handleRowClick}
                        onReport={handlePerformanceReport}
                        onSelectMenu={handleSelectMenu}
                        onScorecardDetail={handleDetail}
                    />
                </Tab>
            </div>
        </ErrorBoundary>
    );
};

export default DailyCheckout;
